import React, { useState, useEffect, useRef } from "react";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	Tabs,
	Tab,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	Switch,
	CircularProgress,
} from "@material-ui/core";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useHistory, useParams } from "react-router-dom";
import {
	deleteBlogPost,
	getBlogPostById,
	postBlogPost,
	putBlogPost,
} from "../../../../api/blog-post";
import { uploadSingleFile } from "../../../../api/files";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { SERVER_URL } from "../../../../utils";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";

const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

const LOCALES = [
	{ key: "es", label: "Español" },
	{ key: "ca", label: "Català" },
	{ key: "en", label: "English" },
];

const QUILL_MODULES = {
	toolbar: [
		[{ header: [1, 2, 3, false] }],
		["bold", "italic", "underline", "strike"],
		[{ list: "ordered" }, { list: "bullet" }],
		["link"],
		["blockquote"],
		["clean"],
	],
};

const QUILL_FORMATS = [
	"header",
	"bold",
	"italic",
	"underline",
	"strike",
	"list",
	"bullet",
	"link",
	"blockquote",
];

function emptyLocalized() {
	return { slug: "", title: "", excerpt: "", contentHtml: "", contentMode: "editor" };
}

function getEmptyPost() {
	return {
		category: "",
		author: "Equipo Evenely",
		coverImageUrl: "",
		readTime: 5,
		status: "draft",
		publishedAt: "",
		es: emptyLocalized(),
		ca: emptyLocalized(),
		en: emptyLocalized(),
	};
}

function paragraphsToHtml(paragraphs) {
	if (!Array.isArray(paragraphs) || !paragraphs.length) return "";
	return paragraphs
		.map((p) => String(p || "").trim())
		.filter(Boolean)
		.map((p) => `<p>${p}</p>`)
		.join("");
}

function toDatetimeLocalValue(value) {
	if (!value) return "";
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return "";
	const pad = (n) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value) {
	if (!value) return null;
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return null;
	return d.toISOString();
}

function resolveCoverPreview(url) {
	const p = String(url || "").trim();
	if (!p) return "";
	if (/^https?:\/\//i.test(p) || p.startsWith("data:")) return p;
	if (p.startsWith("/")) return `${String(SERVER_URL || "").replace(/\/$/, "")}${p}`;
	return `${String(SERVER_URL || "").replace(/\/$/, "")}/${p.replace(/^\/+/, "")}`;
}

function localizedFromApi(loc) {
	const src = loc || {};
	const contentHtml =
		String(src.contentHtml || "").trim() ||
		paragraphsToHtml(src.content) ||
		"";
	return {
		slug: src.slug || "",
		title: src.title || "",
		excerpt: src.excerpt || "",
		contentHtml,
		contentMode: src.contentMode === "html" ? "html" : "editor",
	};
}

function localizedToApi(loc) {
	return {
		slug: loc.slug || "",
		title: loc.title || "",
		excerpt: loc.excerpt || "",
		contentMode: loc.contentMode === "html" ? "html" : "editor",
		contentHtml: String(loc.contentHtml || ""),
		content: [],
	};
}

function resolveStatusFromApi(data) {
	const s = String(data?.status || "").toLowerCase();
	if (s === "draft" || s === "scheduled" || s === "published") return s;
	return data?.published ? "published" : "draft";
}

export default function EditBlogPostPage() {
	const [post, setPost] = useState(getEmptyPost());
	const [tab, setTab] = useState("es");
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [uploadingCover, setUploadingCover] = useState(false);
	const coverInputRef = useRef(null);
	const postId = useParams().id;
	const history = useHistory();

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!postId) {
			disableLoadingData();
			return;
		}
		getBlogPostById(postId)
			.then((res) => {
				if (res.status === 200) {
					const data = res.data;
					setPost({
						category: data.category || "",
						author: data.author || "",
						coverImageUrl: data.coverImageUrl || "",
						readTime: data.readTime || 5,
						status: resolveStatusFromApi(data),
						publishedAt: toDatetimeLocalValue(data.publishedAt),
						es: localizedFromApi(data.es),
						ca: localizedFromApi(data.ca),
						en: localizedFromApi(data.en),
					});
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({ error, customMessage: "No se pudo obtener la entrada." });
				history.push("/blog-posts");
			});
	}, [postId, disableLoadingData, history]);

	function savePost() {
		if (post.status === "scheduled" && !post.publishedAt) {
			alertError({ customMessage: "Indica la fecha y hora de publicación programada." });
			return;
		}

		const body = {
			category: post.category,
			author: post.author,
			coverImageUrl: post.coverImageUrl,
			readTime: Number(post.readTime) || 5,
			status: post.status,
			publishedAt: fromDatetimeLocalValue(post.publishedAt),
			es: localizedToApi(post.es),
			ca: localizedToApi(post.ca),
			en: localizedToApi(post.en),
		};

		if (!postId) {
			postBlogPost(body)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({ title: "Guardado", customMessage: "Entrada creada correctamente." });
						history.push("/blog-posts");
					}
				})
				.catch((error) => {
					alertError({ error, customMessage: "No se pudo crear la entrada." });
				});
		} else {
			putBlogPost(postId, body)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({ title: "Guardado", customMessage: "Cambios guardados correctamente." });
						history.push("/blog-posts");
					}
				})
				.catch((error) => {
					alertError({ error, customMessage: "No se pudieron guardar los cambios." });
				});
		}
	}

	const handleChange = (field) => (event) => {
		const value = event.target.value;
		setPost((prev) => ({ ...prev, [field]: value }));
	};

	/**
	 * Actualiza un campo de un idioma concreto.
	 * Usa la forma funcional de setState para no partir de un `post` obsoleto
	 * del closure: al cambiar de pestaña de idioma eso podía escribir el
	 * contenido de un idioma dentro de otro.
	 */
	const setLocalizedField = (locale, field, value) => {
		setPost((prev) => ({
			...prev,
			[locale]: { ...prev[locale], [field]: value },
		}));
	};

	const handleChangeLocalized = (locale, field) => (event) => {
		const value = event?.target ? event.target.value : event;
		setLocalizedField(locale, field, value);
	};

	const handleCoverUpload = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file) return;
		setUploadingCover(true);
		try {
			const res = await uploadSingleFile(file, "blog");
			const url = res?.data?.fileURL;
			if (!url) throw new Error("Sin URL de archivo");
			setPost((p) => ({ ...p, coverImageUrl: url }));
			alertSuccess({ title: "Imagen", customMessage: "Portada subida correctamente." });
		} catch (error) {
			alertError({ error, customMessage: "No se pudo subir la imagen de portada." });
		} finally {
			setUploadingCover(false);
		}
	};

	if (isLoadingData) return <ContentSkeleton />;

	const current = post[tab];
	const coverPreview = resolveCoverPreview(post.coverImageUrl);

	return (
		<>
			<Card>
				<CardHeader title={postId ? "Editar entrada del blog" : "Nueva entrada del blog"} />
				<CardBody>
					<TextField
						id="category"
						label="Categoría"
						helperText="Ej: Formación, Eventos, Recruitment, Producto"
						value={post.category}
						onChange={handleChange("category")}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
					/>
					<TextField
						id="author"
						label="Autor"
						value={post.author}
						onChange={handleChange("author")}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
					/>
					<TextField
						id="readTime"
						label="Minutos de lectura"
						type="number"
						value={post.readTime}
						onChange={handleChange("readTime")}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
						style={{ width: 180 }}
					/>

					<div style={{ marginTop: 16, marginBottom: 8 }}>
						<div style={{ fontWeight: 600, marginBottom: 8 }}>Imagen de portada</div>
						<input
							ref={coverInputRef}
							type="file"
							accept="image/*"
							style={{ display: "none" }}
							onChange={handleCoverUpload}
						/>
						<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
							<Button
								variant="outlined"
								color="primary"
								disabled={uploadingCover}
								onClick={() => coverInputRef.current?.click()}
							>
								{uploadingCover ? "Subiendo…" : post.coverImageUrl ? "Cambiar imagen" : "Subir imagen"}
							</Button>
							{uploadingCover && <CircularProgress size={22} />}
							{post.coverImageUrl && (
								<Button
									variant="text"
									color="secondary"
									onClick={() => setPost((prev) => ({ ...prev, coverImageUrl: "" }))}
								>
									Quitar
								</Button>
							)}
						</div>
						{coverPreview ? (
							<div style={{ marginTop: 12 }}>
								<img
									src={coverPreview}
									alt="Portada"
									style={{
										maxWidth: 360,
										maxHeight: 200,
										objectFit: "cover",
										borderRadius: 8,
										border: "1px solid #e5e7eb",
									}}
								/>
							</div>
						) : (
							<div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
								Opcional. Se muestra en la ficha de la entrada y en redes / SEO.
							</div>
						)}
					</div>

					<FormControl component="fieldset" style={{ marginTop: 24, display: "block" }}>
						<FormLabel component="legend">Estado</FormLabel>
						<RadioGroup
							row
							value={post.status}
							onChange={(e) => {
								const status = e.target.value;
								setPost((prev) => ({ ...prev, status }));
							}}
						>
							<FormControlLabel value="draft" control={<Radio color="primary" />} label="Borrador" />
							<FormControlLabel value="scheduled" control={<Radio color="primary" />} label="Programada" />
							<FormControlLabel value="published" control={<Radio color="primary" />} label="Publicada" />
						</RadioGroup>
						<div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
							{post.status === "draft" && "No visible en evenely.com/blog."}
							{post.status === "scheduled" &&
								"Se mostrará automáticamente a partir de la fecha/hora indicada."}
							{post.status === "published" && "Visible en evenely.com/blog."}
						</div>
					</FormControl>

					{(post.status === "scheduled" || post.status === "published") && (
						<TextField
							id="publishedAt"
							label={post.status === "scheduled" ? "Publicar a partir de" : "Fecha de publicación"}
							type="datetime-local"
							value={post.publishedAt}
							onChange={handleChange("publishedAt")}
							InputLabelProps={{ shrink: true }}
							margin="normal"
							variant="outlined"
							helperText={
								post.status === "scheduled"
									? "Obligatorio. La entrada aparecerá en el blog a partir de este momento."
									: "Opcional. Si se deja vacío se usará la fecha actual al guardar."
							}
							style={{ maxWidth: 320 }}
						/>
					)}

					<Tabs
						value={tab}
						onChange={(e, value) => setTab(value)}
						indicatorColor="primary"
						textColor="primary"
						style={{ marginTop: 24, marginBottom: 16 }}
					>
						{LOCALES.map((locale) => (
							<Tab key={locale.key} value={locale.key} label={locale.label} />
						))}
					</Tabs>

					<TextField
						id={`${tab}-slug`}
						label={`Slug / URL amigable (${tab})`}
						helperText="Déjalo en blanco para generarlo automáticamente a partir del título. Debe ser único para este idioma."
						value={current.slug}
						onChange={handleChangeLocalized(tab, "slug")}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
						fullWidth
					/>
					<TextField
						id={`${tab}-title`}
						label={`Título (${tab})`}
						value={current.title}
						onChange={handleChangeLocalized(tab, "title")}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
						fullWidth
					/>
					<TextField
						id={`${tab}-excerpt`}
						label={`Extracto / resumen (${tab})`}
						helperText="Se muestra en el listado del blog y en las meta-descripciones"
						value={current.excerpt}
						onChange={handleChangeLocalized(tab, "excerpt")}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
						fullWidth
						multiline
						rows={2}
					/>

					<div style={{ marginTop: 16, marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
						<span style={{ fontWeight: 600 }}>Contenido ({tab})</span>
						<FormControlLabel
							control={
								<Switch
									checked={current.contentMode === "html"}
									onChange={() => {
										setLocalizedField(
											tab,
											"contentMode",
											current.contentMode === "html" ? "editor" : "html"
										);
									}}
									color="primary"
								/>
							}
							label={current.contentMode === "html" ? "Modo HTML" : "Modo editor"}
						/>
					</div>
					<div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
						{current.contentMode === "html"
							? "Pega o escribe HTML directamente. Se renderizará tal cual en la web."
							: "Editor con formato (negrita, listas, enlaces…). El formato se respeta en la web."}
					</div>

					{current.contentMode === "html" ? (
						<TextField
							id={`${tab}-content-html`}
							label={`HTML (${tab})`}
							value={current.contentHtml}
							onChange={handleChangeLocalized(tab, "contentHtml")}
							InputLabelProps={{ shrink: true }}
							margin="normal"
							variant="outlined"
							fullWidth
							multiline
							rows={16}
							inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
						/>
					) : (
						<div style={{ marginTop: 8, marginBottom: 16, background: "#fff" }}>
							{/*
								`key` por idioma: fuerza a React a montar una instancia nueva de
								Quill al cambiar de pestaña, en vez de reutilizar la anterior.
								Sin esto, Quill conserva su estado interno entre idiomas y podía
								volcar el contenido de un idioma sobre otro al cambiar de pestaña.
							*/}
							<ReactQuill
								key={`quill-${tab}`}
								theme="snow"
								value={current.contentHtml || ""}
								onChange={(html, delta, source) => {
									// Quill también emite cambios con source "api"/"silent" cuando se
									// carga el contenido inicial de un idioma. Guardar esos eventos es
									// justo lo que provocaba que se mezclaran los idiomas: solo
									// persistimos ediciones reales del usuario.
									if (source !== "user") return;
									setLocalizedField(tab, "contentHtml", html);
								}}
								modules={QUILL_MODULES}
								formats={QUILL_FORMATS}
								placeholder="Escribe el contenido de la entrada…"
							/>
						</div>
					)}
				</CardBody>
			</Card>

			<Button
				onClick={() => history.push("/blog-posts")}
				variant="outlined"
				style={{ marginRight: 20 }}
			>
				Volver
			</Button>
			<Button onClick={savePost} variant="outlined" color="primary" style={{ marginRight: 20 }}>
				Guardar entrada
			</Button>
			{postId && (
				<>
					<MuiThemeProvider theme={theme}>
						<Button onClick={() => setOpenConfirmDialog(true)} variant="outlined" color="secondary">
							Eliminar entrada
						</Button>
					</MuiThemeProvider>

					<ConfirmDialog
						title={"¿Seguro que quieres eliminar esta entrada?"}
						open={openConfirmDialog}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							deleteBlogPost(postId)
								.then((res) => {
									if (res.status === 204) {
										alertSuccess({ title: "Eliminado", customMessage: "Entrada eliminada correctamente." });
										history.push("/blog-posts");
									}
								})
								.catch((error) => {
									alertError({ error, customMessage: "No se pudo eliminar la entrada." });
								});
						}}
					/>
				</>
			)}
		</>
	);
}
